import { nowIso, type AccidentReportRow, type BulkPickupRow, type Op201FormRow, type ShipperContactRow, type ShipperRow, type TruckRow } from '@routeback/core';
import { Modal, Segmented, useConfirm, useToast } from '@routeback/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { usePeople } from '../../lib/people';
import { fileBytes, usePhotoUrl } from '../../lib/photos';
import { shareText } from '../../lib/share';
import { downloadOp201, fileSafe } from '../../lib/pdf/make';

import { ACCIDENT_STATUS } from '../accident/AccidentDetailView';
import { fileState, fullAddress, locationLabel, type Op201Data } from '../op201/op201';
import { formatRange, todayIn } from '../timeoff/timeoff';

function useRows<T extends { company_id: string; deleted_at: string | null }>(table: string): T[] | undefined {
  const app = useApp();
  return useLiveQuery(async () => (await app.db.table<T, string>(table).where('company_id').equals(app.company.id).toArray()).filter((r) => !r.deleted_at), [app.db, app.company.id, table]);
}
const TONE: Record<string, string> = { good: 'dd-good', warn: 'dd-warn', bad: 'dd-bad', muted: 'dd-missing' };

/**
 * Accident reports for managers: every report drivers have sent, newest
 * first, unreviewed on top; open one for the full packet (photos included)
 * and mark it reviewed. Emails to the company's accident recipients go out
 * in Phase 6; the PDF summary in Phase 5.
 */
export function AccidentsAdminView() {
  const app = useApp();
  const { names } = usePeople();
  const rows = useRows<AccidentReportRow>('accident_reports');
  const trucks = useRows<TruckRow>('trucks');
  const [tab, setTab] = useState<'new' | 'all'>('new');
  if (!rows || !trucks) return <div className="spinner" />;
  const sent = rows.filter((r) => r.status !== 'draft').sort((a, b) => (b.submitted_at ?? '').localeCompare(a.submitted_at ?? ''));
  const shown = tab === 'new' ? sent.filter((r) => r.status === 'submitted') : sent;
  const drafts = rows.filter((r) => r.status === 'draft').length;
  const unit = (id: string | null) => trucks.find((t) => t.id === id)?.unit_number ?? '—';
  return (
    <div>
      <div className="section-title">Accident reports</div>
      <div className="row row-wrap" style={{ margin: '0 12px 8px', gap: 8, justifyContent: 'space-between' }}>
        <Segmented<'new' | 'all'> value={tab} onChange={setTab} options={[{ value: 'new', label: `To review (${sent.filter((r) => r.status === 'submitted').length})` }, { value: 'all', label: 'All' }]} />
        {drafts > 0 && <span className="muted small">{drafts} draft{drafts === 1 ? '' : 's'} not sent yet</span>}
      </div>
      {shown.length === 0 ? <div className="card"><p className="muted" style={{ margin: 0 }}>{tab === 'new' ? 'Nothing to review.' : 'No accident reports yet.'}</p></div> : (
        <div className="list" style={{ margin: '0 12px 12px' }}>
          {shown.map((r) => (
            <Link key={r.id} to={`/requests/accident/${r.id}`} className="list-item settings-link">
              <span style={{ width: 26, textAlign: 'center' }}>🚨</span>
              <div className="grow">
                <div><strong>{r.user_id ? names.get(r.user_id) ?? 'Driver' : 'Former employee'}</strong> · unit {unit(r.truck_id)}</div>
                <div className="muted small">{r.occurred_at ? new Date(r.occurred_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}{r.location ? ` · ${r.location}` : ''}</div>
              </div>
              <span className={'chip' + (r.status === 'reviewed' ? ' on' : '')}>{ACCIDENT_STATUS[r.status]}</span>
            </Link>
          ))}
        </div>
      )}
      <p className="muted small" style={{ margin: '0 14px 16px' }}>Emailing reports to your accident recipients starts with the notifications update; the PDF summary with the PDF update.</p>
    </div>
  );
}

/** Managers mark a sent accident report reviewed (shown on the report itself). */
export function MarkReviewed({ r }: { r: AccidentReportRow }) {
  const app = useApp();
  const toast = useToast();
  if (!app.isManager || r.status === 'draft') return null;
  const toggle = async () => {
    const reviewed = r.status !== 'reviewed';
    await app.repos.accidentReports.save({ ...r, status: reviewed ? 'reviewed' : 'submitted', reviewed_by: reviewed ? app.userId : null, reviewed_at: reviewed ? nowIso() : null });
    toast(reviewed ? 'Marked reviewed' : 'Back to "to review"');
  };
  return <button type="button" className={'btn' + (r.status === 'reviewed' ? '' : ' btn-primary')} disabled={!app.active} onClick={() => void toggle()}>{r.status === 'reviewed' ? 'Undo reviewed' : '✓ Mark reviewed'}</button>;
}

/**
 * OP-201 releases for managers: every release the company has (active,
 * expiring within 60 days, expired, renewed), searchable, with the
 * customer's details and signature; cancel or delete. Sending the PDF to the
 * customer and FedEx comes in Phases 5–6.
 */
export function Op201AdminView() {
  const app = useApp();
  const toast = useToast();
  const confirm = useConfirm();
  const { names } = usePeople();
  const rows = useRows<Op201FormRow>('op201_forms');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Op201FormRow | null>(null);
  if (!rows) return <div className="spinner" />;
  const today = todayIn(app.details.timezone);
  const d = (r: Op201FormRow) => r.data as unknown as Op201Data;
  const s = q.trim().toLowerCase();
  const shown = rows.filter((r) => r.status !== 'cancelled' || s)
    .filter((r) => !s || [d(r).company, d(r).address, d(r).city, d(r).zip, d(r).print_name].some((v) => (v ?? '').toLowerCase().includes(s)))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const op201Pdf = async (r: Op201FormRow) => {
    try {
      const png = r.signature_path ? await fileBytes(r.signature_path, 'signatures') : null;
      const x = d(r);
      const how = await downloadOp201({
        company: x.company, address: x.address, suite: x.suite, city: x.city, state: x.state, zip: x.zip, phone: x.phone, email: x.email,
        location: x.location, location_other: x.location_other, instructions: x.instructions, print_name: x.print_name,
        start_date: x.start_date, sign_date: r.created_at.slice(0, 10), signaturePng: png,
      }, `OP-201_${fileSafe(x.company)}.pdf`);
      if (how === 'downloaded') toast('Saved to your downloads');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Couldn’t make the PDF');
    }
  };
  const cancel = async (r: Op201FormRow) => {
    if (!(await confirm(`Cancel the release for ${d(r).company}? Drivers stop seeing it as on file.`, { ok: 'Cancel release', danger: true }))) return;
    await app.repos.op201Forms.save({ ...r, status: 'cancelled' });
    setOpen(null);
    toast('Release cancelled');
  };
  const remove = async (r: Op201FormRow) => {
    if (!(await confirm(`Delete the release for ${d(r).company}? This can't be undone from the app.`, { ok: 'Delete', danger: true }))) return;
    await app.repos.op201Forms.trash(r.id);
    setOpen(null);
  };
  const csv = () => void shareText(['Company,Address,Signer,Location,Start,Expires,Status,Driver',
    ...shown.map((r) => [d(r).company, fullAddress(d(r)), d(r).print_name, locationLabel(d(r).location), d(r).start_date, r.expires_on ?? '', r.status, r.user_id ? names.get(r.user_id) ?? '' : '']
      .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))].join('\r\n') + '\r\n', 'op201-releases.csv', 'text/csv');
  return (
    <div>
      <div className="section-title">OP-201 releases</div>
      <div className="card row" style={{ gap: 8 }}>
        <input className="grow" aria-label="Search releases" value={q} placeholder="Search company, address, ZIP, signer…" onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="btn btn-sm" disabled={!shown.length} onClick={csv}>↓ CSV</button>
      </div>
      {shown.length === 0 ? <div className="card"><p className="muted" style={{ margin: 0 }}>{rows.length ? 'No releases match.' : 'No OP-201 releases yet — drivers file them from the app.'}</p></div> : (
        <div className="list" style={{ margin: '0 12px 12px' }}>
          {shown.map((r) => {
            const st = r.status === 'cancelled' ? { label: 'Cancelled', tone: 'muted' } : fileState({ status: r.status, expires_on: r.expires_on }, today);
            return (
              <button key={r.id} type="button" className="list-item settings-link fa-row" onClick={() => setOpen(r)}>
                <span style={{ width: 26, textAlign: 'center' }}>✍️</span>
                <div className="grow" style={{ textAlign: 'left' }}>
                  <div><strong>{d(r).company}</strong></div>
                  <div className="muted small">{fullAddress(d(r))}</div>
                </div>
                <span className={'dd-badge ' + TONE[st.tone]}>{st.label}</span>
              </button>
            );
          })}
        </div>
      )}
      <Modal open={!!open} onClose={() => setOpen(null)} title={open ? d(open).company : ''} closeButton>
        {open && (
          <div>
            <dl className="ar-dl">
              <dt>Address</dt><dd>{fullAddress(d(open))}</dd>
              <dt>Contact</dt><dd>{[d(open).phone, d(open).email].filter(Boolean).join(' · ')}</dd>
              <dt>Leave packages at</dt><dd>{locationLabel(d(open).location)}{d(open).location === 'other' ? ` — ${d(open).location_other}` : ''}</dd>
              {d(open).instructions && <><dt>Instructions</dt><dd>{d(open).instructions}</dd></>}
              <dt>Signed by</dt><dd>{d(open).print_name}</dd>
              <dt>Starts / expires</dt><dd>{formatRange(d(open).start_date, null)} → {formatRange(open.expires_on, null)}</dd>
              <dt>Filed by</dt><dd>{open.user_id ? names.get(open.user_id) ?? 'Driver' : '—'} · {new Date(open.created_at).toLocaleDateString()}</dd>
            </dl>
            {open.signature_path && <Signature path={open.signature_path} />}
            <button type="button" className="btn btn-block" style={{ marginTop: 10 }} onClick={() => void op201Pdf(open)}>📄 Download the signed OP-201</button>
            {app.isManager && app.active && (
              <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                {open.status !== 'cancelled' && <button type="button" className="btn btn-sm btn-ghost" onClick={() => void cancel(open)}>Cancel release</button>}
                {app.isAdmin && <button type="button" className="btn btn-sm btn-danger" onClick={() => void remove(open)}>Delete</button>}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Signature({ path }: { path: string }) {
  const url = usePhotoUrl(path, 'signatures');
  return url ? <img src={url} alt="Customer signature" style={{ maxWidth: '100%', background: '#fff', borderRadius: 8, marginTop: 8 }} /> : <p className="muted small">Signature loading…</p>;
}

/**
 * Bulk pickups for managers: the sheets drivers sent (date, shipper, contact,
 * packages, work area, driver) with CSV export, and the shippers + contacts
 * drivers pick from (drivers can add them at the dock; managers tidy them up).
 */
export function BulkAdminView() {
  const app = useApp();
  const toast = useToast();
  const confirm = useConfirm();
  const { names } = usePeople();
  const pickups = useRows<BulkPickupRow>('bulk_pickups');
  const shippers = useRows<ShipperRow>('shippers');
  const contacts = useRows<ShipperContactRow>('shipper_contacts');
  const [tab, setTab] = useState<'sheets' | 'shippers'>('sheets');
  const [editShipper, setEditShipper] = useState<ShipperRow | 'new' | null>(null);
  const [editContact, setEditContact] = useState<{ shipper: ShipperRow; c: ShipperContactRow | null } | null>(null);
  if (!pickups || !shippers || !contacts) return <div className="spinner" />;
  const canEdit = app.isManager && app.active;
  const sheets = pickups.filter((p) => p.status !== 'cancelled').sort((a, b) => b.pickup_date.localeCompare(a.pickup_date) || b.created_at.localeCompare(a.created_at));
  const shipperName = (id: string | null) => shippers.find((s) => s.id === id)?.name ?? '—';
  const contactName = (id: string | null) => contacts.find((c) => c.id === id)?.name ?? '—';
  const csv = () => void shareText(['Date,Shipper,Contact,Packages,Work area,Driver',
    ...sheets.map((p) => [p.pickup_date, shipperName(p.shipper_id), contactName(p.contact_id), p.packages ?? '', String((p.data as Record<string, unknown>)['work_area'] ?? ''), p.user_id ? names.get(p.user_id) ?? '' : '']
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\r\n') + '\r\n', 'bulk-pickups.csv', 'text/csv');
  const removeShipper = async (s: ShipperRow) => {
    if (!(await confirm(`Remove ${s.name} and its contacts? Past pickup sheets keep the name.`, { ok: 'Remove', danger: true }))) return;
    for (const c of contacts.filter((x) => x.shipper_id === s.id)) await app.repos.shipperContacts.trash(c.id);
    await app.repos.shippers.trash(s.id);
    toast('Shipper removed');
  };
  return (
    <div>
      <div className="section-title">Bulk pickups</div>
      <div className="row row-wrap" style={{ margin: '0 12px 8px', gap: 8, justifyContent: 'space-between' }}>
        <Segmented<'sheets' | 'shippers'> value={tab} onChange={setTab} options={[{ value: 'sheets', label: `Pickup sheets (${sheets.length})` }, { value: 'shippers', label: `Shippers (${shippers.length})` }]} />
        {tab === 'sheets' ? <button type="button" className="btn btn-sm" disabled={!sheets.length} onClick={csv}>↓ CSV</button>
          : canEdit && <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditShipper('new')}>+ Shipper</button>}
      </div>
      {tab === 'sheets' && (sheets.length === 0 ? <div className="card"><p className="muted" style={{ margin: 0 }}>No pickup sheets yet.</p></div> : (
        <div className="list" style={{ margin: '0 12px 12px' }}>
          {sheets.map((p) => (
            <div key={p.id} className="list-item">
              <span style={{ width: 26, textAlign: 'center' }}>📦</span>
              <div className="grow">
                <div><strong>{shipperName(p.shipper_id)}</strong> · {p.packages ?? '?'} packages</div>
                <div className="muted small">{formatRange(p.pickup_date, null)} · {contactName(p.contact_id)}{(p.data as Record<string, unknown>)['work_area'] ? ` · ${String((p.data as Record<string, unknown>)['work_area'])}` : ''} · {p.user_id ? names.get(p.user_id) ?? 'Driver' : '—'}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
      {tab === 'shippers' && (shippers.length === 0 ? <div className="card"><p className="muted" style={{ margin: 0 }}>No shippers yet. Drivers can add them at the dock, or add them here.</p></div> : (
        <div className="md-grid">
          {[...shippers].sort((a, b) => a.name.localeCompare(b.name)).map((s) => (
            <div key={s.id} className="md-card md-pri-normal">
              <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                <div><strong>{s.name}</strong>{s.address && <div className="muted small">{s.address}</div>}</div>
                {canEdit && <span className="row" style={{ gap: 4 }}>
                  <button type="button" className="btn btn-sm" onClick={() => setEditShipper(s)}>Edit</button>
                  <button type="button" className="btn btn-sm btn-ghost" aria-label={`Remove ${s.name}`} onClick={() => void removeShipper(s)}>✕</button>
                </span>}
              </div>
              {contacts.filter((c) => c.shipper_id === s.id).map((c) => (
                <div key={c.id} className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
                  <span className="small">👤 {c.name}{c.phone ? ` · ${c.phone}` : ''}{c.signature_path ? ' · ✍️ signature on file' : ''}</span>
                  {canEdit && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditContact({ shipper: s, c })}>Edit</button>}
                </div>
              ))}
              {canEdit && <button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setEditContact({ shipper: s, c: null })}>+ Contact</button>}
            </div>
          ))}
        </div>
      ))}
      <Modal open={!!editShipper} onClose={() => setEditShipper(null)} title={editShipper === 'new' ? 'New shipper' : 'Edit shipper'} closeButton>
        {editShipper && <ShipperForm s={editShipper === 'new' ? null : editShipper} onCancel={() => setEditShipper(null)} onSave={async (f) => {
          if (editShipper === 'new') await app.repos.shippers.save(app.repos.shippers.blank({ ...f, account: '', active: true, route_id: null }));
          else await app.repos.shippers.save({ ...editShipper, ...f });
          setEditShipper(null);
        }} />}
      </Modal>
      <Modal open={!!editContact} onClose={() => setEditContact(null)} title={editContact?.c ? 'Edit contact' : `New contact — ${editContact?.shipper.name ?? ''}`} closeButton>
        {editContact && <ContactForm c={editContact.c} onCancel={() => setEditContact(null)}
          onRemove={editContact.c ? async () => { await app.repos.shipperContacts.trash(editContact.c!.id); setEditContact(null); } : undefined}
          onSave={async (f) => {
            if (editContact.c) await app.repos.shipperContacts.save({ ...editContact.c, ...f });
            else await app.repos.shipperContacts.save(app.repos.shipperContacts.blank({ ...f, shipper_id: editContact.shipper.id, signature_path: null }));
            setEditContact(null);
          }} />}
      </Modal>
    </div>
  );
}

function ShipperForm({ s, onSave, onCancel }: { s: ShipperRow | null; onSave: (f: { name: string; address: string }) => void; onCancel: () => void }) {
  const [name, setName] = useState(s?.name ?? '');
  const [address, setAddress] = useState(s?.address ?? '');
  return (
    <div>
      <div className="field"><label htmlFor="sh-name">Shipper</label><input id="sh-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label htmlFor="sh-addr">Address</label><input id="sh-addr" value={address} onChange={(e) => setAddress(e.target.value)} /></div>
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!name.trim()} onClick={() => onSave({ name: name.trim(), address: address.trim() })}>Save</button>
      </div>
    </div>
  );
}

function ContactForm({ c, onSave, onRemove, onCancel }: { c: ShipperContactRow | null; onSave: (f: { name: string; phone: string; email: string }) => void; onRemove?: () => void; onCancel: () => void }) {
  const [name, setName] = useState(c?.name ?? '');
  const [phone, setPhone] = useState(c?.phone ?? '');
  const [email, setEmail] = useState(c?.email ?? '');
  return (
    <div>
      <div className="field"><label htmlFor="ct-name">Name</label><input id="ct-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="ar-pair">
        <div className="field"><label htmlFor="ct-phone">Phone</label><input id="ct-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        <div className="field"><label htmlFor="ct-email">Email</label><input id="ct-email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      </div>
      <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
        {onRemove ? <button type="button" className="btn btn-sm btn-danger" onClick={onRemove}>Remove</button> : <span />}
        <span className="row" style={{ gap: 8 }}>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!name.trim()} onClick={() => onSave({ name: name.trim(), phone: phone.trim(), email: email.trim() })}>Save</button>
        </span>
      </div>
    </div>
  );
}
